import * as Sentry from "@sentry/react";

export type ErrorPriority = "P1" | "P2" | "P3" | "P4";

type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
type SentryBeforeSend = NonNullable<SentryInitOptions["beforeSend"]>;
type SentryErrorEvent = Parameters<SentryBeforeSend>[0];

const ENTERPRISE_WEB_SERVICE = "enterprise-web";
const PRIORITY_TAG = "aw.priority";
const SESSION_STORAGE_KEY = "assetwatch.enterprise.sessionId";

function parseReplaySampleRate(
  value: string | undefined,
  fallback: number,
): number {
  if (value === null || value === undefined || value.trim() === "") {
    return fallback;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, parsed));
}

function createEnterpriseId(prefix: "req" | "session"): string {
  const crypto = typeof window !== "undefined" ? window.crypto : undefined;

  if (typeof crypto?.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  if (typeof crypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    return `${prefix}-${hex}`;
  }

  return `${prefix}-${Date.now()}`;
}

function getEnterpriseSessionId(): string {
  const fallback = createEnterpriseId("session");
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const next = createEnterpriseId("session");
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return fallback;
  }
}

export function priorityForHttpStatus(statusCode: number): ErrorPriority {
  if (statusCode >= 500) {
    return "P2";
  }
  if (statusCode === 429) {
    return "P3";
  }
  if (statusCode >= 400) {
    return "P4";
  }
  return "P3";
}

function priorityForEvent(event: SentryErrorEvent): ErrorPriority {
  const statusTag = event.tags?.["http.status_code"];
  if (typeof statusTag === "string" || typeof statusTag === "number") {
    const status = Number(statusTag);
    if (Number.isFinite(status)) {
      return priorityForHttpStatus(status);
    }
  }

  return "P2";
}

function withEnterpriseTags(event: SentryErrorEvent): SentryErrorEvent {
  return {
    ...event,
    tags: {
      ...event.tags,
      service: ENTERPRISE_WEB_SERVICE,
      "assetwatch.service": ENTERPRISE_WEB_SERVICE,
      environment: import.meta.env.VITE_DEPLOY_ENVIRONMENT ?? "dev",
      branch: import.meta.env.VITE_BRANCH_NAME,
      [PRIORITY_TAG]: event.tags?.[PRIORITY_TAG] ?? priorityForEvent(event),
    },
  };
}

function apiBreadcrumbLevel(statusCode?: number): "info" | "warning" | "error" {
  if (statusCode !== null && statusCode !== undefined && statusCode >= 500) {
    return "error";
  }
  if (statusCode !== null && statusCode !== undefined && statusCode >= 400) {
    return "warning";
  }
  return "info";
}

function pathnameFromUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

export function createEnterpriseRequestId(): string {
  return createEnterpriseId("req");
}

export function addEnterpriseApiBreadcrumb(input: {
  phase: "request" | "response" | "error";
  method: string;
  url: string;
  requestId: string;
  statusCode?: number;
  durationMs?: number;
  errorName?: string;
}): void {
  if (!Sentry.getClient()) {
    return;
  }

  const path = pathnameFromUrl(input.url);
  Sentry.addBreadcrumb({
    category: "enterprise.api",
    type: "http",
    level: apiBreadcrumbLevel(input.statusCode),
    message: `${input.method} ${path}`,
    data: {
      phase: input.phase,
      path,
      requestId: input.requestId,
      statusCode: input.statusCode,
      durationMs: input.durationMs,
      errorName: input.errorName,
    },
  });
}

export function setEnterpriseCustomerSentryContext(
  externalCustomerId: string | null,
): void {
  if (!Sentry.getClient()) {
    return;
  }

  Sentry.setContext(
    "enterpriseCustomer",
    externalCustomerId ? { externalCustomerId } : null,
  );
  Sentry.setTag(
    "enterprise.external_customer_id",
    externalCustomerId ?? "none",
  );
}

export function setEnterpriseRouteSentryContext(pathname: string): void {
  if (!Sentry.getClient()) {
    return;
  }

  Sentry.setTag("route", pathname);
  Sentry.setContext("enterpriseRoute", { pathname });
}

export function initEnterpriseSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  const branchName = import.meta.env.VITE_BRANCH_NAME;
  const deployEnv = import.meta.env.VITE_DEPLOY_ENVIRONMENT;

  const enterpriseApiBase = (
    import.meta.env.VITE_ENTERPRISE_API_BASE_URL as string | undefined
  )?.replace(/\/$/, "");

  Sentry.init({
    dsn,
    integrations: [
      Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: parseReplaySampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      0.1,
    ),
    environment: deployEnv,
    release: import.meta.env.VITE_COMMIT_ID,
    tracePropagationTargets: enterpriseApiBase
      ? [enterpriseApiBase]
      : [/^https:\/\/enterprise-app\./],
    beforeSend: (event) => withEnterpriseTags(event),
    initialScope: (scope) => {
      const sessionId = getEnterpriseSessionId();
      scope.setTag("service", ENTERPRISE_WEB_SERVICE);
      scope.setTag("assetwatch.service", ENTERPRISE_WEB_SERVICE);
      scope.setTag("environment", deployEnv ?? "dev");
      scope.setContext("enterpriseSession", { id: sessionId });

      if (
        typeof branchName === "string" &&
        branchName.length > 0 &&
        branchName !== deployEnv
      ) {
        scope.setTag("assetwatch.feature-branch", branchName);
        scope.setTag("branch", branchName);
      }
      return scope;
    },
  });

  if (!import.meta.env.DEV) {
    const client = Sentry.getClient();
    if (client?.addIntegration) {
      client.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true,
        }),
      );
    }
  }
}

export type AskReliSentryContext = {
  customerId: number;
  phase: "fetch" | "http_response" | "json_parse";
  requestId?: string;
  httpStatus?: number;
  responsePreview?: string;
};

export function captureAskReliError(
  error: unknown,
  context: AskReliSentryContext,
): void {
  if (!Sentry.getClient()) {
    return;
  }

  const err = error instanceof Error ? error : new Error(String(error));
  const priority =
    context.httpStatus === null || context.httpStatus === undefined
      ? "P3"
      : priorityForHttpStatus(context.httpStatus);
  Sentry.withScope((scope) => {
    scope.setTag("feature", "ask-reli");
    scope.setTag(PRIORITY_TAG, priority);
    scope.setTag("customer_id", String(context.customerId));
    if (context.httpStatus !== null && context.httpStatus !== undefined) {
      scope.setTag("http.status_code", String(context.httpStatus));
    }
    if (context.requestId) {
      scope.setTag("request_id", context.requestId);
    }
    scope.setContext("askReli", context);
    Sentry.captureException(err);
  });
}
